import React from 'react';
import { connect } from 'react-redux';
import { setMonth, setYear, format } from 'date-fns';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import TableViewIcon from '@mui/icons-material/TableView';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { withStyles } from '@mui/styles';

import { LoadingView } from './LoadingView';
import { TitleTooltip } from './TitleTooltip';

import {
  StoreUtil,
} from 'doctivity-shared/utils';
import {
  ColorUtil,
  CsvDownloadUtil,
  utcDayRange,
} from 'utils';

import {
  loadAnalytics,
} from 'store/actions/analyticsActions';

// The two affiliation charts on the Visits page. `side` picks which provider on the
// visit is judged affiliated or not: the one who made the referral, or the one who
// received it. Each has its own report type, so both can be on screen at once.
// (The 'provider' side is not mounted on the Visits page for now.)
//
// The page's two label filters (Provider Label, Referring Provider Label) apply to both
// sides.
const SIDES = {
  referrer: {
    type: 'REFERRING_AFFILIATION_BY_MONTH',
    title: 'Monthly Referring Provider Affiliation',
    axis: 'Visits',
    none: 'No Referrer',
    file: 'referring_provider_affiliation_by_month',
    info: 'Visits each month, stacked by the provider who made the referral: '
      + 'affiliated with your organization, non-affiliated, or no referring provider '
      + 'on the visit (No Referrer).',
  },
  provider: {
    type: 'REFERRED_AFFILIATION_BY_MONTH',
    title: 'Monthly Referred Provider Affiliation',
    axis: 'Visits',
    none: 'No Referred Provider',
    file: 'referred_provider_affiliation_by_month',
    info: 'Visits each month, stacked by the provider who received the referral: '
      + 'affiliated with your organization, non-affiliated, or no referred provider '
      + 'on the visit.',
  },
};


const monthLabel = (row) => `${format(setMonth(new Date(), row.month), 'MMM')} ${format(setYear(new Date(), row.year), 'yy')}`;
const rowTotal = (row) => (Number(row.affiliated) || 0)
  + (Number(row.non_affiliated) || 0)
  + (Number(row.none) || 0);
const fmt = (n) => Number(n || 0).toLocaleString();

// Neutral grey for visits with no provider on that side, next to the standard
// affiliated / non-affiliated colors.
const NONE_COLOR = '#B5BCC6';

const styles = (theme) => ({
  chartWrapper: {
    '& .apexcharts-legend': {
      display: 'flex !important',
      flexDirection: 'row !important',
      flexWrap: 'nowrap !important',
      justifyContent: 'center !important',
      alignItems: 'center !important',
      gap: '0 !important',
    },
    '& .apexcharts-legend-group': {
      display: 'inline-flex !important',
      flexDirection: 'row !important',
      margin: '0 !important',
      padding: '0 !important',
    },
    '& .apexcharts-legend-series': {
      display: 'inline-flex !important',
      margin: '0 6px !important',
      whiteSpace: 'nowrap !important',
    },
    '& .apexcharts-legend-text': {
      fontSize: '12px !important',
    },
  },
  card: {
    marginTop: theme.spacing(3),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  spacer: {
    flex: 1,
  },
  totalRow: {
    '& td': {
      fontWeight: 500,
    },
  },
  nothingFound: {
    fontWeight: 300,
    fontSize: 12,
  },
});

class ReferringAffiliationOverTimeChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isTable: false,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.clientId !== prevProps.clientId
      || this.props.app.dateRange !== prevProps.app.dateRange
      || this.props.department !== prevProps.department
      || this.props.referringOrganization !== prevProps.referringOrganization
      || this.props.providerLabelId !== prevProps.providerLabelId
      || this.props.referrerLabelId !== prevProps.referrerLabelId
      || this.props.side !== prevProps.side) {
      this.fetchData();
    }
  }

  fetchData() {
    this.props.dispatch(loadAnalytics({
      type: SIDES[this.props.side].type,
      filter: {
        client_id: this.props.clientId,
        department: this.props.department,
        referring_organization: this.props.referringOrganization,
        provider_tag_id: this.props.providerLabelId,
        referrer_tag_id: this.props.referrerLabelId,
        ...utcDayRange(this.props.app.dateRange),
      },
    }));
  }

  render() {
    const {
      classes,
      data,
      side,
    } = this.props;

    let graph;
    if (!data || StoreUtil.isLoading(data)) {
      graph = (<LoadingView />);
    } else if (data.data && data.data.length > 0) {
      const rows = StoreUtil.getData(data);
      // An area needs two points to draw anything, so a one-month range (or a click
      // into a single month) shows the same stack as a single column instead.
      const chartType = rows.length > 1 ? 'area' : 'bar';
      const labels = {
        formatter: (value) => {
          return value.toLocaleString();
        },
      };
      graph = (
        <div className={classes.chartWrapper}>
          <Chart
            key={chartType}
            type={chartType}
            height={260}
            options={{
              chart: {
                id: `${side}_affiliation_over_time`,
                stacked: true,
                toolbar: {
                  show: false,
                },
                zoom: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: rows.map(monthLabel),
              },
              tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
              },
              colors: [ColorUtil.AffiliatedColor, ColorUtil.NonAffiliatedColor, NONE_COLOR],
              stroke: {
                curve: 'smooth',
                width: chartType === 'area' ? 2 : 0,
              },
              plotOptions: {
                bar: {
                  columnWidth: '30%',
                },
              },
              fill: {
                type: 'solid',
                opacity: 0.75,
              },
              dataLabels: {
                enabled: false,
              },
              legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                itemMargin: {
                  horizontal: 10,
                  vertical: 0,
                },
                markers: {
                  width: 12,
                  height: 12,
                },
              },
              yaxis: {
                title: {
                  text: SIDES[side].axis,
                },
                labels,
              },
            }}
            series={[
              {
                name: 'Affiliated',
                data: rows.map((row) => row.affiliated),
              },
              {
                name: 'Non-Affiliated',
                data: rows.map((row) => row.non_affiliated),
              },
              {
                name: SIDES[side].none,
                data: rows.map((row) => row.none),
              },
            ]}
          />
        </div>
      );
      // Table view (header toggle) shows the same rows instead of the chart.
      if (this.state.isTable) {
        graph = this.renderTable(rows);
      }
    } else {
      graph = (
        <Typography className={classes.nothingFound}>
          No referring affiliation data available.
        </Typography>
      );
    }

    return (
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.header}>
            <TitleTooltip
              title={SIDES[side].title}
              tooltip={SIDES[side].info}
              onExport={this.onExport}
            />
            <div className={classes.spacer} />
            <IconButton
              onClick={() => this.setState({ isTable: !this.state.isTable })}
              size='large'
            >
              {this.state.isTable ? (
                <Tooltip title='Show Chart'>
                  <ViewModuleIcon />
                </Tooltip>
              ) : (
                <Tooltip title='Show Table'>
                  <TableViewIcon />
                </Tooltip>
              )}
            </IconButton>
          </div>

          {graph}

        </CardContent>
      </Card>
    );
  }

  // Same months and series as the chart, plus a Total column and row.
  renderTable(rows) {
    const { classes, side } = this.props;
    const sum = (key) => rows.reduce((s, row) => s + (Number(row[key]) || 0), 0);
    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Month</TableCell>
            <TableCell align='right'>Affiliated</TableCell>
            <TableCell align='right'>Non-Affiliated</TableCell>
            <TableCell align='right'>{SIDES[side].none}</TableCell>
            <TableCell align='right'>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.year}-${row.month}`}>
              <TableCell>{monthLabel(row)}</TableCell>
              <TableCell align='right'>{fmt(row.affiliated)}</TableCell>
              <TableCell align='right'>{fmt(row.non_affiliated)}</TableCell>
              <TableCell align='right'>{fmt(row.none)}</TableCell>
              <TableCell align='right'>{fmt(rowTotal(row))}</TableCell>
            </TableRow>
          ))}
          <TableRow className={classes.totalRow}>
            <TableCell>Total</TableCell>
            <TableCell align='right'>{fmt(sum('affiliated'))}</TableCell>
            <TableCell align='right'>{fmt(sum('non_affiliated'))}</TableCell>
            <TableCell align='right'>{fmt(sum('none'))}</TableCell>
            <TableCell align='right'>{fmt(rows.reduce((s, row) => s + rowTotal(row), 0))}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  onExport = () => {
    const { data, side, app } = this.props;
    // While a refetch is loading, the store still holds the previous range's rows; skip
    // rather than save them under the new range's file name.
    if (StoreUtil.isLoading(data)) {
      return;
    }
    const rows = StoreUtil.getData(data) || [];
    CsvDownloadUtil.download(
      CsvDownloadUtil.fileName(SIDES[side].file, app.dateRange),
      ['Month', 'Affiliated', 'Non-Affiliated', SIDES[side].none, 'Total'],
      rows.map((row) => [
        CsvDownloadUtil.month(row),
        row.affiliated,
        row.non_affiliated,
        row.none,
        rowTotal(row),
      ]),
    );
  };
}

ReferringAffiliationOverTimeChart.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object,
  app: PropTypes.object,
  data: PropTypes.object,
  clientId: PropTypes.number.isRequired,
  department: PropTypes.string,
  referringOrganization: PropTypes.string,
  providerLabelId: PropTypes.number,
  referrerLabelId: PropTypes.number,
  side: PropTypes.oneOf(Object.keys(SIDES)).isRequired,
};

function mapStateToProps(state, ownProps) {
  return {
    app: state.app,
    data: StoreUtil.get(state.analytics, SIDES[ownProps.side].type),
  };
}

const styled = withStyles(styles)(ReferringAffiliationOverTimeChart);
const connected = connect(mapStateToProps)(styled);
export { connected as ReferringAffiliationOverTimeChart };
